import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, Save } from 'lucide-react';
import {
  SPELLING_ERROR_TYPES_ENGLISH,
  SPELLING_ERROR_TYPES_KANNADA,
  WRITING_LEGIBILITY_OPTIONS,
} from './WritingScreeningOptions';

const COLORS = {
  navyBlue: '#003366',
  orange: '#FF6B35',
  mustard: '#FFAA00',
  green: '#16A34A',
  lightGray: '#F9FAFB',
  white: '#FFFFFF',
  borderGray: '#D1D5DB',
  textGray: '#6B7280',
  textDark: '#1F2937',
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const ageYearsOnDate = (dob, referenceDate) => {
  try {
    if (!dob || !referenceDate) return null;
    const [dd, mm, yyyy] = dob.split('/').map(Number);
    const [rdd, rmm, ryyyy] = referenceDate.split('/').map(Number);
    if (![dd, mm, yyyy, rdd, rmm, ryyyy].every(Number.isFinite)) return null;
    let years = ryyyy - yyyy;
    if (rmm < mm || (rmm === mm && rdd < dd)) years -= 1;
    return years;
  } catch {
    return null;
  }
};

const numericValue = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatMetric = (value) => Number.isFinite(value)
  ? (Number.isInteger(value) ? String(value) : value.toFixed(1))
  : '—';

const dashLookupRound = (value) => Number.isFinite(value) ? Math.floor(value + 0.5) : null;

const INITIAL_WRITING_DATA = {
  copySentence: '',
  copyBestTotalWords: '',
  copyFastTotalWords: '',
  freeWritingTopic: '',
  totalWords: '',
  legibleWords: '',
  illegibleWords: '',
  spellingErrors: '',
  alphabetWriting: '',
  writingLegibility: '',
  freeWritingExample1Correct: '', freeWritingExample1SpeltAs: '', freeWritingExample1ErrorType: '',
  freeWritingExample2Correct: '', freeWritingExample2SpeltAs: '', freeWritingExample2ErrorType: '',
  freeWritingExample3Correct: '', freeWritingExample3SpeltAs: '', freeWritingExample3ErrorType: '',
  freeWritingExample4Correct: '', freeWritingExample4SpeltAs: '', freeWritingExample4ErrorType: '',
  freeWritingExample5Correct: '', freeWritingExample5SpeltAs: '', freeWritingExample5ErrorType: '',
  freeWritingAdditionalNotes: '',
  pencilGrip: '', sittingPosture: '', paperPosition: '',
  writingHesitation: '', writingCrossedOut: '', writingLookingUp: '',
  writingHandFatigue: '', writingFrustration: '', writingFluency: '',
  afterIdeasClearly: '', afterLogicalSequence: '', afterVocabularyRange: '',
  languagePlanning: '', languageGrammar: '', languageSentenceStructure: '', languageCapitalisation: '',
  sentenceStarterVariety: '', useOfConnectives: '',
  mechanicsLetterFormation: '', mechanicsSizeConsistency: '', mechanicsSpacingBetweenWords: '', mechanicsLineAlignment: '',
};

// Keep the same required fields that the former Baseline Writing section used.
// The migration changes where the assessment lives and how it is stored, not
// the assessment content or its completion rules.
const REQUIRED_FIELDS = [
  'copySentence', 'copyBestTotalWords', 'copyFastTotalWords', 'freeWritingTopic',
  'totalWords', 'legibleWords', 'illegibleWords', 'spellingErrors', 'writingLegibility', 'alphabetWriting',
  'pencilGrip', 'sittingPosture', 'paperPosition',
  'writingHesitation', 'writingCrossedOut', 'writingLookingUp', 'writingHandFatigue', 'writingFrustration', 'writingFluency',
  'afterIdeasClearly', 'afterLogicalSequence', 'afterVocabularyRange',
  'languagePlanning', 'languageGrammar', 'languageSentenceStructure', 'languageCapitalisation',
  'sentenceStarterVariety', 'useOfConnectives',
  'mechanicsLetterFormation', 'mechanicsSizeConsistency', 'mechanicsSpacingBetweenWords', 'mechanicsLineAlignment',
];

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: {
    backgroundColor: COLORS.navyBlue,
    color: COLORS.white,
    padding: '8px 16px',
    borderBottom: `2px solid ${COLORS.mustard}`,
  },
  headerContent: {
    maxWidth: '1080px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '8px 0',
  },
  body: { maxWidth: '1080px', margin: '0 auto', padding: '24px' },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    padding: '24px',
    marginBottom: '20px',
  },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  smallLabel: { display: 'block', fontSize: '12px', fontWeight: '600', color: COLORS.textDark, marginBottom: '5px' },
  input: {
    width: '100%', padding: '9px 12px', border: `2px solid ${COLORS.borderGray}`,
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: COLORS.white,
  },
  select: {
    width: '100%', padding: '9px 12px', border: `2px solid ${COLORS.borderGray}`,
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: COLORS.white,
  },
  textarea: {
    width: '100%', padding: '10px 12px', border: `2px solid ${COLORS.borderGray}`,
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical',
  },
  button: {
    backgroundColor: COLORS.orange, color: COLORS.white, padding: '12px 24px', borderRadius: '6px',
    fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '16px',
  },
  errorBox: {
    backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B',
    padding: '12px 16px', borderRadius: '6px', marginBottom: '16px',
  },
};

const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' };

export default function WritingScreening({
  student,
  apiUrl,
  facilitatorId,
  facilitatorName,
  schoolId,
  schoolName,
  programmeId,
  programmeName,
  initialTimepoint,
  correctionMode = false,
  onBack,
  onSaved,
}) {
  const [stage, setStage] = useState('entry');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const timepoint = initialTimepoint || 'Baseline';
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [data, setData] = useState(INITIAL_WRITING_DATA);
  const [result, setResult] = useState(null);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionLoaded, setCorrectionLoaded] = useState(true);
  const [effectiveCorrectionMode, setEffectiveCorrectionMode] = useState(false);

  useEffect(() => {
    if (!student?.student_code) return;
    let cancelled = false;
    setCorrectionLoading(true);
    setCorrectionLoaded(false);
    setEffectiveCorrectionMode(false);
    setError('');

    fetchWithTimeout(`${apiUrl}/api/writing/record/${student.student_code}?timepoint=${encodeURIComponent(timepoint)}`)
      .then(async (res) => {
        const responseData = await res.json().catch(() => ({}));
        if (res.status === 404) return { noRecord: true };
        if (!res.ok) throw new Error(responseData.error || 'Could not load the saved Writing Assessment');
        return responseData;
      })
      .then((responseData) => {
        if (cancelled) return;
        if (responseData?.noRecord) {
          setCorrectionLoaded(true);
          return;
        }
        if (!responseData?.correction_allowed) {
          setCorrectionLoaded(false);
          setError('Writing Assessment is already saved and locked. Reopen this screener for correction before editing it.');
          return;
        }
        const saved = responseData.writing_data || {};
        setData((prev) => ({ ...prev, ...saved }));
        if (responseData.screening_date) setScreeningDate(responseData.screening_date);
        if (responseData.administration_mode) setAdministrationMode(responseData.administration_mode);
        if (responseData.assessor_name) setAssessorName(responseData.assessor_name);
        setNotes(responseData.notes || '');
        setEffectiveCorrectionMode(true);
        setCorrectionLoaded(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setCorrectionLoaded(false);
          setEffectiveCorrectionMode(false);
          setError(e.message || 'Could not load the saved Writing Assessment');
        }
      })
      .finally(() => { if (!cancelled) setCorrectionLoading(false); });

    return () => { cancelled = true; };
  }, [student?.student_code, apiUrl, timepoint, correctionMode]);

  const update = (key, value) => setData((prev) => ({ ...prev, [key]: value }));
  const baseErrorTypes = student?.medium_of_instruction === 'Kannada'
    ? SPELLING_ERROR_TYPES_KANNADA
    : SPELLING_ERROR_TYPES_ENGLISH;
  const errorTypes = Array.from(new Set([...baseErrorTypes, 'Visual error']));

  const ageYears = ageYearsOnDate(student?.dob, screeningDate);
  const copySentenceOptions = ageYears !== null && ageYears >= 17 ? ['A3', 'A4'] : ['A1', 'A2'];
  const copyBestTotal = numericValue(data.copyBestTotalWords);
  const copyFastTotal = numericValue(data.copyFastTotalWords);
  const legibleWords = numericValue(data.legibleWords);
  const illegibleWords = numericValue(data.illegibleWords);
  const copyBestWpm = copyBestTotal === null ? null : copyBestTotal / 2;
  const copyFastWpm = copyFastTotal === null ? null : copyFastTotal / 2;
  const freeWritingWpm = legibleWords === null ? null : legibleWords / 10;
  const copySpeedDifference = copyBestWpm === null || copyFastWpm === null ? null : copyFastWpm - copyBestWpm;
  const totalLegibilityWords = legibleWords === null || illegibleWords === null ? null : legibleWords + illegibleWords;
  const percentIllegible = totalLegibilityWords && illegibleWords !== null
    ? (illegibleWords / totalLegibilityWords) * 100
    : (totalLegibilityWords === 0 ? 0 : null);
  const spellingErrorCount = numericValue(data.spellingErrors);
  const requiredSpellingExamples = spellingErrorCount === null ? 0 : Math.min(5, Math.max(0, Math.floor(spellingErrorCount)));

  const validate = () => {
    const missing = REQUIRED_FIELDS.filter((key) => data[key] === null || data[key] === undefined || String(data[key]).trim() === '');
    if (missing.length > 0) {
      setError('Please complete all required Writing Assessment fields before saving.');
      return false;
    }
    if (spellingErrorCount === null || !Number.isInteger(spellingErrorCount) || spellingErrorCount < 0) {
      setError('Spelling errors must be entered as a whole-number count.');
      return false;
    }
    for (let n = 1; n <= requiredSpellingExamples; n += 1) {
      const correct = String(data[`freeWritingExample${n}Correct`] || '').trim();
      const speltAs = String(data[`freeWritingExample${n}SpeltAs`] || '').trim();
      const errorType = String(data[`freeWritingExample${n}ErrorType`] || '').trim();
      if (!correct || !speltAs || !errorType) {
        setError(`Please complete spelling example ${n}. Only ${requiredSpellingExamples} example${requiredSpellingExamples === 1 ? '' : 's'} ${requiredSpellingExamples === 1 ? 'is' : 'are'} required for the recorded error count.`);
        return false;
      }
    }
    if (!copySentenceOptions.includes(data.copySentence)) {
      setError(`Please select the copying sentence for the student's age group (${copySentenceOptions.join(' or ')}).`);
      return false;
    }
    const wholeCountFields = [
      ['copyBestTotalWords', 'Best writing total words'],
      ['copyFastTotalWords', 'Everyday writing total words'],
      ['legibleWords', 'Free Writing legible words'],
      ['illegibleWords', 'Free Writing illegible words'],
      ['alphabetWriting', 'Alphabet writing letters'],
    ];
    for (const [key, label] of wholeCountFields) {
      const value = numericValue(data[key]);
      if (value === null || !Number.isInteger(value) || value < 0) {
        setError(`${label} must be entered as a whole-number count.`);
        return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/writing/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code,
          screening_date: screeningDate,
          timepoint,
          school_id: schoolId || student?.school_id || '',
          school_name: schoolName || '',
          programme_id: programmeId || student?.programme_id || '',
          programme_name: programmeName || '',
          assessor_name: assessorName,
          administration_mode: administrationMode,
          administration_batch_id: administrationBatchId,
          entered_by_user_id: facilitatorId,
          entered_by_name: facilitatorName,
          notes,
          correction_mode: effectiveCorrectionMode,
          writing_data: data,
        }),
      });
      const responseData = await res.json();
      if (!res.ok) throw new Error(responseData.error || 'Could not save this Writing Assessment');
      setResult(responseData);
      setStage('complete');
      if (onSaved) onSaved(responseData);
    } catch (e) {
      setError(e.message || 'Could not save this Writing Assessment');
    } finally {
      setLoading(false);
    }
  };

  const Header = ({ title }) => (
    <div style={styles.header}>
      <div style={styles.headerContent}>
        <button style={styles.backButton} onClick={onBack}>
          <ArrowLeft size={18} />
          Back to {student.full_name}
        </button>
        <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
      </div>
    </div>
  );

  if (stage === 'complete') {
    return (
      <div style={styles.container}>
        <Header title="Writing Assessment: Saved" />
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '16px' }}>
              <CheckCircle size={28} />
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Saved</h2>
            </div>

            {result?.dash2_score?.success && (
              <div style={{ backgroundColor: '#F8FAFC', borderRadius: '6px', padding: '14px', marginBottom: '18px' }}>
                <div><strong>Total Standard Score:</strong> {result.dash2_score.total_standard_score}</div>
                <div><strong>Percentile:</strong> {result.dash2_score.percentile_rank}</div>
                <div><strong>95% CI:</strong> {result.dash2_score.ci_95?.join('–')}</div>
                <div><strong>Descriptor:</strong> {result.dash2_score.band_descriptor}</div>
              </div>
            )}
            {result?.dash2_scoring_status === 'manual_review_required' && (
              <div style={styles.errorBox}>
                Writing responses were saved, but the standard score needs review: {result?.dash2_score?.reason || 'automatic scoring was not available.'}
              </div>
            )}
            <button
              style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }}
              onClick={onBack}
            >
              Back to {student.full_name}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header title="Writing Assessment" />
      <div style={styles.body}>
        <div style={styles.card}>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 6px' }}>
            Writing Assessment - {student.full_name}
          </h1>
          <p style={{ color: COLORS.textGray, marginTop: 0, marginBottom: '20px' }}>Timepoint: {timepoint}</p>

          {error && <div style={styles.errorBox}>{error}</div>}
          {effectiveCorrectionMode && correctionLoaded && (
            <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', color: '#8A5A2B', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', textAlign: 'center' }}>
              Correction mode - previous values loaded. Change only what is needed, then save the correction.
            </div>
          )}

          <div style={gridStyle}>
            <div>
              <label style={styles.label}>Screening date (DD/MM/YYYY)</label>
              <input style={styles.input} type="text" value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Administration mode</label>
              <select style={styles.select} value={administrationMode} onChange={(e) => setAdministrationMode(e.target.value)}>
                <option value="Individual">Individual (1:1)</option>
                <option value="Group">Group</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Assessor name</label>
              <input style={styles.input} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{ ...styles.card, border: '1px solid #DCE6F3' }}>
          <h2 style={{ color: COLORS.navyBlue, fontSize: '18px', marginTop: 0, marginBottom: '6px' }}>Writing Speed</h2>
          <p style={{ color: COLORS.textGray, marginTop: 0, fontSize: '13px' }}>
            Enter the counted legible-word totals from the paper administration. Count a partly written final word as one whole word. The system calculates WPM and, for the norm lookup, rounds to the nearest whole number with .5 rounded up. Use the same sentence for Best writing and Everyday writing.
          </p>
          <div style={gridStyle}>
            <div>
              <label style={styles.label}>Chosen sentence *</label>
              <select style={styles.select} value={data.copySentence} onChange={(e) => update('copySentence', e.target.value)}>
                <option value="">Select</option>
                {copySentenceOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <div style={{ fontSize: '12px', color: COLORS.textGray, marginTop: '5px' }}>
                {ageYears === null ? 'Sentence options shown for the younger age band until DOB/date can be resolved.' : `Age on screening date: ${ageYears} years`}
              </div>
            </div>
            <div>
              <label style={styles.label}>Best writing (2 mins) *</label>
              <input style={styles.input} type="number" min="0" step="1" value={data.copyBestTotalWords} onChange={(e) => update('copyBestTotalWords', e.target.value)} />
              <div style={{ fontSize: '12px', color: COLORS.textGray, marginTop: '5px' }}>WPM: <strong>{formatMetric(copyBestWpm)}</strong> · norm lookup: <strong>{dashLookupRound(copyBestWpm) ?? '—'}</strong></div>
            </div>
            <div>
              <label style={styles.label}>Everyday writing (2 mins) *</label>
              <input style={styles.input} type="number" min="0" step="1" value={data.copyFastTotalWords} onChange={(e) => update('copyFastTotalWords', e.target.value)} />
              <div style={{ fontSize: '12px', color: COLORS.textGray, marginTop: '5px' }}>WPM: <strong>{formatMetric(copyFastWpm)}</strong> · norm lookup: <strong>{dashLookupRound(copyFastWpm) ?? '—'}</strong></div>
            </div>
            <div>
              <label style={styles.label}>Free writing *</label>
              <select style={styles.select} value={data.freeWritingTopic} onChange={(e) => update('freeWritingTopic', e.target.value)}>
                <option value="">Select</option>
                <option value="My Life">My Life (ML)</option>
                <option value="Past/Present/Future">Past / Present / Future (PPF)</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
            <div style={{ padding: '10px', borderRadius: '6px', backgroundColor: '#F8FAFC' }}>Range between Everyday - Best writing: <strong>{formatMetric(copySpeedDifference === null ? null : Math.abs(copySpeedDifference))} WPM</strong></div>
            <div style={{ padding: '10px', borderRadius: '6px', backgroundColor: '#F8FAFC' }}>Free Writing formal WPM: <strong>{formatMetric(freeWritingWpm)}</strong> · norm lookup: <strong>{dashLookupRound(freeWritingWpm) ?? '—'}</strong></div>
            <div style={{ padding: '10px', borderRadius: '6px', backgroundColor: '#F8FAFC' }}>Illegible words: <strong>{formatMetric(percentIllegible)}%</strong></div>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={{ color: COLORS.navyBlue, fontSize: '18px', marginTop: 0 }}>Writing output</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px', alignItems: 'stretch' }}>
            {[
              ['totalWords', 'Total words written (10 mins)'],
              ['legibleWords', 'Legible words (Free Writing)'],
              ['illegibleWords', 'Illegible words'],
              ['spellingErrors', 'Spelling errors'],
              ['alphabetWriting', 'Alphabet writing'],
            ].map(([key, label]) => (
              <div key={key} style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ ...styles.label, minHeight: '34px', display: 'flex', alignItems: 'flex-end' }}>{label} *</label>
                <input style={styles.input} type="number" min="0" value={data[key]} onChange={(e) => update(key, e.target.value)} />
              </div>
            ))}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ ...styles.label, minHeight: '34px', display: 'flex', alignItems: 'flex-end' }}>Writing legibility *</label>
              <select style={styles.select} value={data.writingLegibility} onChange={(e) => update('writingLegibility', e.target.value)}>
                <option value="">Select</option>
                {WRITING_LEGIBILITY_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ ...styles.card, backgroundColor: '#F8FAFF', border: '1px solid #E0E7FF' }}>
          <h2 style={{ color: COLORS.navyBlue, fontSize: '18px', marginTop: 0, marginBottom: '4px' }}>Free Writing Spelling Examples</h2>
          <p style={{ color: COLORS.textGray, marginTop: 0, fontSize: '13px' }}>
            Enter up to 5 spelling-error examples. If fewer than 5 errors were found, enter only the examples that exist. For Kannada, use English letters (Roman script).
          </p>
          <div style={gridStyle}>
            {Array.from({ length: 5 }, (_, i) => i + 1).map((n) => (
              <div key={n} style={{ backgroundColor: COLORS.white, border: '1px solid #E0E7FF', borderRadius: '6px', padding: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: COLORS.navyBlue, marginBottom: '9px' }}>
                  Example {n}{n <= requiredSpellingExamples ? ' *' : ''}
                </div>
                <label style={styles.smallLabel}>Correct</label>
                <input
                  style={{ ...styles.input, marginBottom: '8px' }}
                  value={data[`freeWritingExample${n}Correct`]}
                  onChange={(e) => update(`freeWritingExample${n}Correct`, e.target.value.replace(/\d/g, ''))}
                  autoComplete="off"
                />
                <label style={styles.smallLabel}>Spelt as</label>
                <input
                  style={{ ...styles.input, marginBottom: '8px' }}
                  value={data[`freeWritingExample${n}SpeltAs`]}
                  onChange={(e) => update(`freeWritingExample${n}SpeltAs`, e.target.value.replace(/\d/g, ''))}
                  autoComplete="off"
                />
                <label style={styles.smallLabel}>Error type{n <= requiredSpellingExamples ? ' *' : ''}</label>
                <select style={styles.select} value={data[`freeWritingExample${n}ErrorType`]} onChange={(e) => update(`freeWritingExample${n}ErrorType`, e.target.value)}>
                  <option value="">Select type</option>
                  {errorTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '14px' }}>
            <label style={styles.label}>Additional notes</label>
            <textarea style={styles.textarea} rows="2" value={data.freeWritingAdditionalNotes} onChange={(e) => update('freeWritingAdditionalNotes', e.target.value)} />
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={{ color: COLORS.navyBlue, fontSize: '18px', marginTop: 0 }}>Free writing observations</h2>

          <h3 style={{ fontSize: '13px', color: COLORS.textGray }}>Physical and behavioural aspects</h3>
          <div style={gridStyle}>
            <div>
              <label style={styles.smallLabel}>Pencil grip</label>
              <select style={styles.select} value={data.pencilGrip} onChange={(e) => update('pencilGrip', e.target.value)}>
                <option value="">Select</option>
                {['Functional tripod', 'Quadrupod', 'Thumb wrap/lateral', 'Immature four-grip', 'Fisted', 'Unstable/switching'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.smallLabel}>Sitting posture</label>
              <select style={styles.select} value={data.sittingPosture} onChange={(e) => update('sittingPosture', e.target.value)}>
                <option value="">Select</option>
                {['Upright and stable (feet flat and back supported)', 'Slightly slouched or leaning', 'Poor posture (significant leaning, unstable positioning)'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.smallLabel}>Paper position</label>
              <select style={styles.select} value={data.paperPosition} onChange={(e) => update('paperPosition', e.target.value)}>
                <option value="">Select</option>
                {['Slanted left', 'Slanted right', 'Upright'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          </div>

          <h3 style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '20px' }}>Writing behaviour</h3>
          <div style={gridStyle}>
            <div><label style={styles.smallLabel}>Hesitation / pausing</label><select style={styles.select} value={data.writingHesitation} onChange={(e) => update('writingHesitation', e.target.value)}><option value="">Select</option>{['Never', 'Sometimes', 'Often'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Words crossed out (number)</label><input style={styles.input} type="number" min="0" value={data.writingCrossedOut} onChange={(e) => update('writingCrossedOut', e.target.value)} /></div>
            <div><label style={styles.smallLabel}>Looking up/around</label><select style={styles.select} value={data.writingLookingUp} onChange={(e) => update('writingLookingUp', e.target.value)}><option value="">Select</option>{['Never', 'Sometimes', 'Often'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Hand fatigue</label><select style={styles.select} value={data.writingHandFatigue} onChange={(e) => update('writingHandFatigue', e.target.value)}><option value="">Select</option>{['Never', 'Sometimes', 'Often'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Frustration</label><select style={styles.select} value={data.writingFrustration} onChange={(e) => update('writingFrustration', e.target.value)}><option value="">Select</option>{['Never', 'Sometimes', 'Often'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div>
              <label style={styles.smallLabel}>Writing fluency</label>
              <select style={styles.select} value={data.writingFluency} onChange={(e) => update('writingFluency', e.target.value)}>
                <option value="">Select</option>
                {[
                  'Fluent \u2014 writes continuously with natural flow',
                  'Moderate \u2014 some pauses but generally steady',
                  'Laboured \u2014 slow, effortful, frequent stops',
                ].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          </div>

          <h3 style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '20px' }}>After writing</h3>
          <div style={gridStyle}>
            <div><label style={styles.smallLabel}>Ideas expressed clearly</label><select style={styles.select} value={data.afterIdeasClearly} onChange={(e) => update('afterIdeasClearly', e.target.value)}><option value="">Select</option>{['Good', 'Below expected', 'Poor'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Logical sequence</label><select style={styles.select} value={data.afterLogicalSequence} onChange={(e) => update('afterLogicalSequence', e.target.value)}><option value="">Select</option>{['As expected', 'Weak'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Vocabulary range</label><select style={styles.select} value={data.afterVocabularyRange} onChange={(e) => update('afterVocabularyRange', e.target.value)}><option value="">Select</option>{['Excellent', 'Good', 'Average', 'Weak'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
          </div>

          <h3 style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '20px' }}>Language skills</h3>
          <div style={gridStyle}>
            <div><label style={styles.smallLabel}>Planning for writing</label><select style={styles.select} value={data.languagePlanning} onChange={(e) => update('languagePlanning', e.target.value)}><option value="">Select</option>{['Strong', 'Good', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Grammar usage</label><select style={styles.select} value={data.languageGrammar} onChange={(e) => update('languageGrammar', e.target.value)}><option value="">Select</option>{['Strong', 'Good', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Sentence structure</label><select style={styles.select} value={data.languageSentenceStructure} onChange={(e) => update('languageSentenceStructure', e.target.value)}><option value="">Select</option>{['Strong', 'Good', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Capitalisation</label><select style={styles.select} value={data.languageCapitalisation} onChange={(e) => update('languageCapitalisation', e.target.value)}><option value="">Select</option>{['Excellent', 'Good', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Sentence starter variety</label><select style={styles.select} value={data.sentenceStarterVariety} onChange={(e) => update('sentenceStarterVariety', e.target.value)}><option value="">Select</option>{['Varied', 'Limited', 'Repetitive'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Use of connectives</label><select style={styles.select} value={data.useOfConnectives} onChange={(e) => update('useOfConnectives', e.target.value)}><option value="">Select</option>{['Appropriate', 'Emerging', 'Limited'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
          </div>

          <h3 style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '20px' }}>Writing mechanics</h3>
          <div style={gridStyle}>
            <div><label style={styles.smallLabel}>Letter formation</label><select style={styles.select} value={data.mechanicsLetterFormation} onChange={(e) => update('mechanicsLetterFormation', e.target.value)}><option value="">Select</option>{['Strong', 'Developing', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Size consistency</label><select style={styles.select} value={data.mechanicsSizeConsistency} onChange={(e) => update('mechanicsSizeConsistency', e.target.value)}><option value="">Select</option>{['Strong', 'Developing', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Spacing between words</label><select style={styles.select} value={data.mechanicsSpacingBetweenWords} onChange={(e) => update('mechanicsSpacingBetweenWords', e.target.value)}><option value="">Select</option>{['Good', 'Inconsistent', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label style={styles.smallLabel}>Line alignment</label><select style={styles.select} value={data.mechanicsLineAlignment} onChange={(e) => update('mechanicsLineAlignment', e.target.value)}><option value="">Select</option>{['Good', 'Inconsistent', 'Needs support'].map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
          </div>
        </div>

        <div style={styles.card}>
          <label style={styles.label}>Recommendations / notes</label>
          <textarea style={styles.textarea} rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <button
            style={{ ...styles.button, marginTop: '16px', opacity: (loading || correctionLoading || !correctionLoaded) ? 0.6 : 1 }}
            onClick={submit}
            disabled={loading || correctionLoading || !correctionLoaded}
          >
            <Save size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            {loading ? 'Saving...' : (effectiveCorrectionMode ? 'Save Correction' : 'Save and lock Writing Assessment')}
          </button>
        </div>
      </div>
    </div>
  );
}
